import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post, del } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader } from "@/components/common/PageHeader";
import { FormDialog } from "@/components/common/FormDialog";
import { ConfirmDelete } from "@/components/common/ConfirmDelete";
import { Button } from "@/components/ui/button";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { dateFmt } from "@/lib/format";
import { FileText, Eye, Search, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Engineering", "Sales", "Design", "Finance", "HR"];

export default function KnowledgeBase() {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [tag, setTag] = useState("all");
  const qc = useQueryClient();
  const { can } = useApp();
  const { data: articles, isLoading } = useQuery({ queryKey: ["kb", q], queryFn: () => get(`/kb${q ? `?q=${encodeURIComponent(q)}` : ""}`) });

  const tags = useMemo(() => [...new Set((articles || []).flatMap((a) => a.tags || []))], [articles]);

  const visibleArticles = useMemo(() => {
    return (articles || []).filter((a) => {
      if (category !== "all" && a.category !== category) return false;
      if (tag !== "all" && !(a.tags || []).includes(tag)) return false;
      return true;
    });
  }, [articles, category, tag]);

  const create = useMutation({ mutationFn: (b) => post("/kb", b), onSuccess: () => { qc.invalidateQueries({ queryKey: ["kb"] }); toast.success("Article published"); } });
  const remove = useMutation({ mutationFn: (id) => del(`/kb/${id}`), onSuccess: () => { qc.invalidateQueries({ queryKey: ["kb"] }); toast.success("Article deleted"); } });

  return (
    <div>
      <PageHeader title="Knowledge Base" subtitle="Company SOPs, playbooks and policies.">
        <FormDialog title="Upload Article" testid="upload-article-modal"
          trigger={<Button data-testid="upload-article-btn"><Plus className="mr-1.5 h-4 w-4" />Upload Article</Button>}
          fields={[
            { name: "title", label: "Title", required: true, full: true },
            { name: "category", label: "Category", type: "select", required: true, options: ["Engineering", "Sales", "Design", "Finance", "HR"] },
          ]}
          onSubmit={(v) => create.mutateAsync({ ...v, tags: [v.category.toLowerCase()], uploaded_by: "u-avery" })} />
      </PageHeader>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative w-72">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="kb-search" placeholder="Search articles" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-40" data-testid="kb-category-filter"><SelectValue placeholder="All categories" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={tag} onValueChange={setTag}>
          <SelectTrigger className="w-40" data-testid="kb-tag-filter"><SelectValue placeholder="All tags" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tags</SelectItem>
            {tags.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {isLoading ? <Skeleton className="h-64" /> : !articles?.length ? (
        <EmptyState icon={FileText} title="No articles yet" />
      ) : !visibleArticles.length ? (
        <EmptyState icon={FileText} title="No articles match" description="Adjust your search or filters." />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {visibleArticles.map((a) => (
            <div key={a.id} data-testid={`kb-${a.id}`} className="rounded-xl border border-border bg-card p-4 transition-all hover:border-primary/40 hover:shadow-sm">
              <div className="flex items-start justify-between">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary"><FileText className="h-5 w-5" /></div>
                <span className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">{a.category}</span>
              </div>
              <h3 className="mt-3 font-display text-sm font-semibold leading-snug">{a.title}</h3>
              <div className="mt-2 flex flex-wrap gap-1">{a.tags.map((t) => <span key={t} className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">{t}</span>)}</div>
              <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><Eye className="h-3 w-3" />{a.views}</span>
                <div className="flex items-center gap-2">
                  <span>Updated {dateFmt(a.updated_at)}</span>
                  {can("kb", "delete") && (
                    <ConfirmDelete testid={`kb-del-${a.id}`} title="Delete article?"
                      trigger={<button data-testid={`kb-del-btn-${a.id}`} className="text-muted-foreground hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>}
                      onConfirm={() => remove.mutate(a.id)} />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
